import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Radio,
  Button,
  Typography,
  Space,
  message,
  RadioChangeEvent,
} from "antd";
import {
  ClockCircleOutlined,
  TrophyOutlined,
  SoundOutlined,
  UserOutlined,
  HomeOutlined,
  AppleOutlined,
  AndroidOutlined,
  StopOutlined,
} from "@ant-design/icons";
import Axios from "axios";
import jwtDecode from "jwt-decode";
import { Helmet } from "react-helmet-async";
import {
  useLanguage,
  Language,
  LanguageProvider,
  translations,
} from "../Home/LanguageContext";

const { Title, Text, Paragraph } = Typography;

const COLORS = {
  background: "#1A1A2E",
  primary: "#F65283",
  secondary: "#252541",
  textColor: "#FFFFFF",
  lightText: "#8F8FA0",
  grey: "#3E3E52",
  red: "#FF5C8D",
  green: "#64C864",
  white: "#ffffff",
  orange: "#FFB84D",
  darkCard: "#252541",
  accentPink: "#FF5C8D",
};

interface Question {
  id: number;
  type: "grammar" | "listening" | "vocabulary";
  question: string;
  options: string[];
  correctAnswer: number;
  level: "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
  audioUrl?: string;
}

interface QuestionBank {
  language: string;
  languageName: string;
  questions: Question[];
}

const loadQuestionBank = async (languageCode: string): Promise<Question[]> => {
  try {
    const response = await import(`./questions/${languageCode}.json`);
    const data: QuestionBank = response.default;
    return data.questions;
  } catch (error) {
    console.error(`Failed to load questions for ${languageCode}:`, error);
    return [];
  }
};

const selectRandomQuestions = (questionBank: Question[]): Question[] => {
  if (questionBank.length === 0) {
    return [];
  }

  const questionsByLevel: { [key: string]: Question[] } = {
    A1: questionBank.filter((q) => q.level === "A1"),
    A2: questionBank.filter((q) => q.level === "A2"),
    B1: questionBank.filter((q) => q.level === "B1"),
    B2: questionBank.filter((q) => q.level === "B2"),
    C1: questionBank.filter((q) => q.level === "C1"),
    C2: questionBank.filter((q) => q.level === "C2"),
  };

  const shuffle = (array: Question[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const selected: Question[] = [];
  selected.push(...shuffle(questionsByLevel.A1).slice(0, 2));
  selected.push(...shuffle(questionsByLevel.A2).slice(0, 2));
  selected.push(...shuffle(questionsByLevel.B1).slice(0, 4));
  selected.push(...shuffle(questionsByLevel.B2).slice(0, 3));
  selected.push(...shuffle(questionsByLevel.C1).slice(0, 3));
  selected.push(...shuffle(questionsByLevel.C2).slice(0, 2));

  return selected.map((q, index) => ({ ...q, id: index + 1 }));
};

const CEFR_LEVELS = [
  { level: "A1", min: 0, max: 25, label: "Beginner" },
  { level: "A2", min: 26, max: 45, label: "Elementary" },
  { level: "B1", min: 46, max: 65, label: "Intermediate" },
  { level: "B2", min: 66, max: 80, label: "Upper-Intermediate" },
  { level: "C1", min: 81, max: 90, label: "Advanced" },
  { level: "C2", min: 91, max: 100, label: "Proficient" },
];

const LANGUAGE_NAMES: { [key: string]: string } = {
  "en-us": "English",
  "es-es": "Spanish (Español)",
  "fr-fr": "French (Français)",
  "de-de": "German (Deutsch)",
  "it-it": "Italian (Italiano)",
  "tr-tr": "Turkish (Türkçe)",
  "ja-jp": "Japanese (日本語)",
  "ko-kr": "Korean (한국어)",
  "zh-cn": "Chinese (中文)",
};

const TEST_DURATION = 8 * 60;

// Translated language names based on current language
const getTranslatedLanguageName = (
  languageCode: string,
  currentLang: string,
): string => {
  const translations: { [key: string]: { [key: string]: string } } = {
    en: {
      "en-us": "English",
      "es-es": "Spanish",
      "fr-fr": "French",
      "de-de": "German",
      "it-it": "Italian",
      "tr-tr": "Turkish",
      "ja-jp": "Japanese",
      "ko-kr": "Korean",
      "zh-cn": "Chinese",
    },
    fa: {
      "en-us": "انگلیسی",
      "es-es": "اسپانیایی",
      "fr-fr": "فرانسوی",
      "de-de": "آلمانی",
      "it-it": "آلمانی",
      "tr-tr": "آلمانی",
      "ja-jp": "ژاپنی",
      "ko-kr": "کره‌ای",
      "zh-cn": "چینی",
    },
    ar: {
      "en-us": "الإنجليزية",
      "es-es": "الإسبانية",
      "fr-fr": "الفرنسية",
      "de-de": "الألمانية",
      "it-it": "الألمانية",
      "tr-tr": "الألمانية",
      "ja-jp": "اليابانية",
      "ko-kr": "الكورية",
      "zh-cn": "الصينية",
    },
    ko: {
      "en-us": "영어",
      "es-es": "스페인어",
      "fr-fr": "프랑스어",
      "de-de": "독일어",
      "it-it": "독일어",
      "tr-tr": "독일어",
      "ja-jp": "일본어",
      "ko-kr": "한국어",
      "zh-cn": "중국어",
    },
    de: {
      "en-us": "Englisch",
      "es-es": "Spanisch",
      "fr-fr": "Französisch",
      "de-de": "Deutsch",
      "it-it": "Deutsch",
      "tr-tr": "Deutsch",
      "ja-jp": "Japanisch",
      "ko-kr": "Koreanisch",
      "zh-cn": "Chinesisch",
    },
    fr: {
      "en-us": "Anglais",
      "es-es": "Espagnol",
      "fr-fr": "Français",
      "de-de": "Allemand",
      "it-it": "Allemand",
      "tr-tr": "Allemand",
      "ja-jp": "Japonais",
      "ko-kr": "Coréen",
      "zh-cn": "Chinois",
    },
    ru: {
      "en-us": "Английский",
      "es-es": "Испанский",
      "fr-fr": "Французский",
      "de-de": "Немецкий",
      "it-it": "Немецкий",
      "tr-tr": "Немецкий",
      "ja-jp": "Японский",
      "ko-kr": "Корейский",
      "zh-cn": "Китайский",
    },
    zh: {
      "en-us": "英语",
      "es-es": "西班牙语",
      "fr-fr": "法语",
      "de-de": "德语",
      "it-it": "德语",
      "tr-tr": "德语",
      "ja-jp": "日语",
      "ko-kr": "韩语",
      "zh-cn": "中文",
    },
    ja: {
      "en-us": "英語",
      "es-es": "スペイン語",
      "fr-fr": "フランス語",
      "de-de": "ドイツ語",
      "it-it": "ドイツ語",
      "tr-tr": "ドイツ語",
      "ja-jp": "日本語",
      "ko-kr": "韓国語",
      "zh-cn": "中国語",
    },
    it: {
      "en-us": "Inglese",
      "es-es": "Spagnolo",
      "fr-fr": "Francese",
      "de-de": "Tedesco",
      "it-it": "Tedesco",
      "tr-tr": "Tedesco",
      "ja-jp": "Giapponese",
      "ko-kr": "Coreano",
      "zh-cn": "Cinese",
    },
    tr: {
      "en-us": "İngilizce",
      "es-es": "İspanyolca",
      "fr-fr": "Fransızca",
      "de-de": "Almanca",
      "it-it": "Almanca",
      "tr-tr": "Almanca",
      "ja-jp": "Japonca",
      "ko-kr": "Korece",
      "zh-cn": "Çince",
    },
  };

  return (
    translations[currentLang]?.[languageCode] ||
    translations.en[languageCode] ||
    LANGUAGE_NAMES[languageCode] ||
    languageCode
  );
};

// Download App Section Component
const DownloadAppSection: React.FC<{
  t: (key: keyof typeof translations.en) => string;
}> = ({ t }) => {
  return (
    <div
      style={{
        marginTop: 32,
        textAlign: "center",
        maxWidth: 600,
        width: "100%",
        padding: "24px",
        background: "rgba(255, 255, 255, 0.03)",
        borderRadius: 16,
        border: `1px solid ${COLORS.grey}`,
      }}
    >
      <Paragraph
        style={{
          color: COLORS.textColor,
          marginBottom: 20,
          fontSize: 16,
          fontWeight: "500",
        }}
      >
        {t("saveResultsTrackProgress")}
      </Paragraph>
      <Space
        size="middle"
        style={{ justifyContent: "center", flexWrap: "wrap" }}
      >
        <Button
          type="primary"
          size="large"
          icon={<AppleOutlined style={{ fontSize: 18 }} />}
          onClick={() =>
            window.open(
              "https://apps.apple.com/us/app/lingua-talk/id6746571204",
              "_blank",
            )
          }
          style={{
            background: "white",
            color: COLORS.primary,
            borderColor: "white",
            fontWeight: "700",
            height: 48,
            fontSize: 15,
            borderRadius: 24,
            padding: "0 28px",
            boxShadow: "0 4px 12px rgba(246, 82, 131, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 6px 20px rgba(246, 82, 131, 0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(246, 82, 131, 0.3)";
          }}
        >
          {t("downloadForIOS")}
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<AndroidOutlined style={{ fontSize: 18 }} />}
          onClick={() =>
            window.open(
              "https://play.google.com/store/apps/details?id=com.parsa.lingua_talk",
              "_blank",
            )
          }
          style={{
            background: "white",
            color: COLORS.primary,
            borderColor: "white",
            fontWeight: "700",
            height: 48,
            fontSize: 15,
            borderRadius: 24,
            padding: "0 28px",
            boxShadow: "0 4px 12px rgba(246, 82, 131, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 6px 20px rgba(246, 82, 131, 0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(246, 82, 131, 0.3)";
          }}
        >
          {t("downloadForAndroid")}
        </Button>
      </Space>
      <Paragraph
        style={{
          color: COLORS.textColor,
          marginTop: 20,
          fontSize: 14,
          fontWeight: "500",
        }}
      >
        {t("improveWithApp")}
      </Paragraph>
    </div>
  );
};

const PlacementTest: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [timeLeft, setTimeLeft] = useState(TEST_DURATION);
  const [isCompleted, setIsCompleted] = useState(false);
  const [result, setResult] = useState<{
    level: string;
    score: number;
    label: string;
  } | null>(null);
  const [token, setToken] = useState<string>("");
  const [languageCode, setLanguageCode] = useState<string>("");
  const [languageName, setLanguageName] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [, setIsSubmitting] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [, setAudioInstance] = useState<HTMLAudioElement | null>(null);
  const audioInstanceRef = useRef<HTMLAudioElement | null>(null);
  const isStoppingAudioRef = useRef<boolean>(false);
  const [hasError, setHasError] = useState<string>("");
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const { language, setLanguage, t } = useLanguage();
  const [translatedLanguageName, setTranslatedLanguageName] =
    useState<string>("");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const initializeTest = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token");
      const urlLanguage = params.get("targetLanguage");
      const appLang = params.get("app_lang");

      if (!urlLanguage) {
        setHasError(t("unableToLoadTest"));
        setIsLoading(false);
        return;
      }

      const availableLanguages: Language[] = [
        "en",
        "fa",
        "ar",
        "ko",
        "de",
        "fr",
        "ru",
        "zh",
        "ja",
        "it",
        "tr",
      ];

      if (appLang) {
        const lang = appLang.toLowerCase();
        if (availableLanguages.includes(lang as Language)) {
          setLanguage(lang as Language);
        } else {
          setLanguage(language);
        }
      } else {
        setLanguage(language);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps

      const langCode = urlLanguage.toLowerCase();
      setLanguageCode(langCode);
      setLanguageName(LANGUAGE_NAMES[langCode] || urlLanguage);
      const currentLang =
        appLang &&
        availableLanguages.includes(appLang.toLowerCase() as Language)
          ? appLang.toLowerCase()
          : language;
      setTranslatedLanguageName(
        getTranslatedLanguageName(langCode, currentLang),
      );

      if (urlToken) {
        setToken(urlToken);
        try {
          const decoded: any = jwtDecode(urlToken);
          setUserName(
            decoded.name || decoded.username || decoded.email || "User",
          );
        } catch (error) {
          console.error("Failed to decode token:", error);
          setUserName("");
        }
      } else {
        setUserName("");
      }

      if (langCode !== "en-us") {
        setHasError("NOT_IMPLEMENTED");
        setIsLoading(false);
        return;
      }

      const questionBank = await loadQuestionBank(langCode);

      if (questionBank.length === 0) {
        setHasError(t("unableToLoadTest"));
        setIsLoading(false);
        return;
      }

      const selectedQuestions = selectRandomQuestions(questionBank);
      setQuestions(selectedQuestions);
      setIsLoading(false);
    };

    initializeTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (languageCode && language) {
      setTranslatedLanguageName(
        getTranslatedLanguageName(languageCode, language),
      );
    }
  }, [language, languageCode]);

  useEffect(() => {
    if (countdown !== null && countdown > 1) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 1) {
      setTimeout(() => {
        setShowPreview(false);
        setCountdown(null);
      }, 1000);
    }
  }, [countdown]);

  const calculateResult = useCallback(() => {
    const levelWeights = {
      A1: 1,
      A2: 2,
      B1: 3,
      B2: 4,
      C1: 5,
      C2: 6,
    };

    let totalWeight = 0;
    let earnedWeight = 0;

    questions.forEach((q) => {
      const weight = levelWeights[q.level];
      totalWeight += weight;
      if (answers[q.id] === q.correctAnswer) {
        earnedWeight += weight;
      }
    });

    const score = Math.round((earnedWeight / totalWeight) * 100);
    const cefrLevel =
      CEFR_LEVELS.find((level) => score >= level.min && score <= level.max) ||
      CEFR_LEVELS[0];

    return {
      level: cefrLevel.level,
      score,
      label: cefrLevel.label,
    };
  }, [answers, questions]);

  const handleSubmit = useCallback(async () => {
    const testResult = calculateResult();
    setResult(testResult);
    setIsCompleted(true);

    if (token) {
      setIsSubmitting(true);
      try {
        await Axios.post(
          "/api/users/level",
          {
            level: testResult.level,
            score: testResult.score,
            language: languageCode,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        message.success(t("testResultSaved"));
      } catch (error) {
        message.error(t("testResultFailed"));
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [calculateResult, token, languageCode, t]);

  const stopAudio = useCallback(() => {
    const currentAudio = audioInstanceRef.current;
    if (currentAudio) {
      isStoppingAudioRef.current = true;
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio.onplay = null;
        currentAudio.oncanplaythrough = null;
        currentAudio.onended = null;
        currentAudio.onerror = null;
        currentAudio.onabort = null;
      } catch (error) {
        console.error("Error stopping audio:", error);
      }
      audioInstanceRef.current = null;
      setAudioInstance(null);
      setAudioPlaying(false);
      setTimeout(() => {
        isStoppingAudioRef.current = false;
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (isCompleted || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isCompleted, timeLeft, handleSubmit]);

  useEffect(() => {
    const currentAudio = audioInstanceRef.current;
    if (currentAudio) {
      isStoppingAudioRef.current = true;
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio.onplay = null;
        currentAudio.oncanplaythrough = null;
        currentAudio.onended = null;
        currentAudio.onerror = null;
        currentAudio.onabort = null;
      } catch (error) {
        console.error("Error stopping audio on question change:", error);
      }
      audioInstanceRef.current = null;
      setAudioInstance(null);
      setAudioPlaying(false);
      setTimeout(() => {
        isStoppingAudioRef.current = false;
      }, 100);
    }
  }, [currentStep]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleAnswerChange = (e: RadioChangeEvent) => {
    setAnswers({
      ...answers,
      [questions[currentStep].id]: e.target.value,
    });
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const playAudio = () => {
    const currentQuestion = questions[currentStep];
    if (currentQuestion.type === "listening" && currentQuestion.audioUrl) {
      if (audioPlaying) {
        return;
      }

      if (audioInstanceRef.current) {
        const prevAudio = audioInstanceRef.current;
        isStoppingAudioRef.current = true;
        try {
          prevAudio.pause();
          prevAudio.currentTime = 0;
          prevAudio.onplay = null;
          prevAudio.oncanplaythrough = null;
          prevAudio.onended = null;
          prevAudio.onerror = null;
          prevAudio.onabort = null;
        } catch (error) {
          console.error("Error stopping previous audio:", error);
        }
        audioInstanceRef.current = null;
        setAudioInstance(null);
        setTimeout(() => {
          isStoppingAudioRef.current = false;
        }, 100);
      }

      setAudioPlaying(true);

      const audioUrl = currentQuestion.audioUrl.startsWith("http")
        ? currentQuestion.audioUrl
        : `${window.location.origin}${currentQuestion.audioUrl}`;

      const audio = new Audio(audioUrl);
      audioInstanceRef.current = audio;
      setAudioInstance(audio);

      audio.onerror = (e) => {
        if (isStoppingAudioRef.current) {
          return;
        }
        console.error("Audio error:", e, audio.error);
        const errorMsg = audio.error
          ? `Audio error: ${audio.error.code} - ${audio.error.message}`
          : "Failed to load audio file";
        message.error(errorMsg);
        setAudioPlaying(false);
        audioInstanceRef.current = null;
        setAudioInstance(null);
      };

      audio.oncanplaythrough = () => {
        if (isStoppingAudioRef.current || audioInstanceRef.current !== audio) {
          return;
        }
        audio.play().catch((err) => {
          if (isStoppingAudioRef.current) {
            return;
          }
          console.error("Audio play error:", err);
          message.error(
            "Failed to play audio. Please check your browser permissions.",
          );
          setAudioPlaying(false);
          audioInstanceRef.current = null;
          setAudioInstance(null);
        });
      };

      audio.onended = () => {
        setAudioPlaying(false);
        audioInstanceRef.current = null;
        setAudioInstance(null);
      };

      audio.onabort = () => {
        console.error("Audio aborted");
        setAudioPlaying(false);
        audioInstanceRef.current = null;
        setAudioInstance(null);
      };

      audio.load();
    }
  };

  const handleTryAgain = () => {
    setCurrentStep(0);
    setAnswers({});
    setTimeLeft(TEST_DURATION);
    setIsCompleted(false);
    setResult(null);
  };

  const handleContinue = () => {
    window.location.href = "/";
  };

  const handleStartTest = () => {
    setCountdown(3);
  };

  const getTestStatistics = () => {
    if (questions.length === 0) return null;

    const parts = questions.reduce(
      (acc, q) => {
        acc[q.type] = (acc[q.type] || 0) + 1;
        return acc;
      },
      {} as { [key: string]: number },
    );

    return {
      totalQuestions: questions.length,
      totalTime: TEST_DURATION,
      parts: parts,
    };
  };

  const currentQuestion = questions[currentStep];
  const testStats = getTestStatistics();

  if (countdown !== null) {
    return (
      <>
        <Helmet>
          <title>
            {t("testStarting")} | {translatedLanguageName} {t("placementTest")}
          </title>
        </Helmet>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            background: COLORS.background,
            position: "relative",
          }}
        >
          <div
            style={{
              textAlign: "center",
              position: "relative",
            }}
          >
            <Title
              level={1}
              style={{
                margin: 0,
                fontSize: 120,
                color: COLORS.primary,
                fontWeight: "bold",
                lineHeight: 1,
                marginBottom: 30,
                animation: "pulse 1s ease-in-out infinite",
              }}
            >
              {countdown}
            </Title>
            <Title
              level={2}
              style={{
                color: COLORS.textColor,
                marginTop: 20,
              }}
            >
              {t("testStarting")}
            </Title>
            <Paragraph
              style={{
                color: COLORS.lightText,
                fontSize: 16,
                marginTop: 10,
              }}
            >
              {t("getReady")}
            </Paragraph>
          </div>
          <style>
            {`
              @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.1); opacity: 0.8; }
              }
            `}
          </style>
        </div>
      </>
    );
  }

  if (showPreview && testStats && !isLoading) {
    return (
      <>
        <Helmet>
          <title>
            {t("testPreview")} | {translatedLanguageName} {t("placementTest")}
          </title>
          <meta
            name="description"
            content={`${t("testPreview")} ${translatedLanguageName} ${t(
              "placementTest",
            )}. ${testStats.totalQuestions} ${t("questions")}, ${Math.floor(
              testStats.totalTime / 60,
            )} ${t("min")}.`}
          />
        </Helmet>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            padding: "20px",
            background: COLORS.background,
          }}
        >
          <Card
            style={{
              maxWidth: 600,
              width: "100%",
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              background: COLORS.darkCard,
              border: `1px solid ${COLORS.grey}`,
            }}
          >
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div style={{ textAlign: "center" }}>
                <Title
                  level={2}
                  style={{
                    color: COLORS.textColor,
                    marginBottom: 8,
                    textAlign: "center",
                  }}
                >
                  {t("testForLanguage")} {translatedLanguageName}
                </Title>
                <Paragraph style={{ color: COLORS.lightText, fontSize: 16 }}>
                  {t("testYourLanguageProficiency")}
                </Paragraph>
                <Space style={{ marginTop: 8 }}>
                  <UserOutlined
                    style={{ color: COLORS.lightText, fontSize: 14 }}
                  />
                  <Text style={{ color: COLORS.lightText, fontSize: 14 }}>
                    {userName || t("guest")}
                  </Text>
                </Space>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginTop: "20px",
                }}
              >
                <div
                  style={{
                    background: COLORS.secondary,
                    padding: "20px",
                    borderRadius: 12,
                    border: `1px solid ${COLORS.grey}`,
                    textAlign: "center",
                  }}
                >
                  <Text
                    style={{
                      color: COLORS.lightText,
                      fontSize: 14,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    {t("totalQuestions")}
                  </Text>
                  <Title
                    level={1}
                    style={{
                      margin: 0,
                      color: COLORS.primary,
                      fontSize: 36,
                    }}
                  >
                    {testStats.totalQuestions}
                  </Title>
                </div>

                <div
                  style={{
                    background: COLORS.secondary,
                    padding: "20px",
                    borderRadius: 12,
                    border: `1px solid ${COLORS.grey}`,
                    textAlign: "center",
                  }}
                >
                  <Text
                    style={{
                      color: COLORS.lightText,
                      fontSize: 14,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    {t("totalTime")}
                  </Text>
                  <Title
                    level={1}
                    style={{
                      margin: 0,
                      color: COLORS.primary,
                      fontSize: 36,
                    }}
                  >
                    {Math.floor(testStats.totalTime / 60)} {t("min")}
                  </Title>
                </div>
              </div>

              <div
                style={{
                  background: COLORS.secondary,
                  padding: "20px",
                  borderRadius: 12,
                  border: `1px solid ${COLORS.grey}`,
                  marginTop: "16px",
                }}
              >
                <Text
                  style={{
                    color: COLORS.lightText,
                    fontSize: 14,
                    display: "block",
                    marginBottom: 16,
                    textAlign: "center",
                  }}
                >
                  {t("testParts")}
                </Text>
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  {Object.entries(testStats.parts).map(([part, count]) => (
                    <div
                      key={part}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px",
                        background: COLORS.background,
                        borderRadius: 8,
                      }}
                    >
                      <Text
                        style={{
                          color: COLORS.textColor,
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      >
                        {t(part as "grammar" | "vocabulary" | "listening")}
                      </Text>
                      <Text
                        style={{
                          color: COLORS.white,
                          fontSize: 16,
                          fontWeight: "bold",
                        }}
                      >
                        {count} {count === 1 ? t("question") : t("questions")}
                      </Text>
                    </div>
                  ))}
                </Space>
              </div>

              <Button
                type="primary"
                size="large"
                onClick={handleStartTest}
                style={{
                  width: "100%",
                  height: 50,
                  fontSize: 18,
                  fontWeight: "bold",
                  background: COLORS.primary,
                  borderColor: COLORS.primary,
                  marginTop: "20px",
                }}
              >
                {t("startTest")}
              </Button>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                {(() => {
                  const params = new URLSearchParams(window.location.search);
                  const token = params.get("token");
                  const appLang = params.get("app_lang");
                  return (
                    <a
                      href={`/placement-test?targetLanguage=0${
                        token ? `&token=${token}` : ""
                      }${appLang ? `&app_lang=${appLang}` : ""}`}
                      style={{
                        color: COLORS.primary,
                        fontSize: 13,
                        textDecoration: "none",
                        opacity: 0.8,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "0.8";
                      }}
                    >
                      {t("viewAllLanguages")}
                    </a>
                  );
                })()}
              </div>
            </Space>
          </Card>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading Placement Test | LinguaTalk</title>
          <meta
            name="description"
            content="Preparing your language placement test. Test your language skills across all CEFR levels from A1 to C2."
          />
        </Helmet>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            padding: "12px",
            background: COLORS.background,
          }}
        >
          <Card
            style={{
              maxWidth: 600,
              width: "100%",
              textAlign: "center",
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              background: COLORS.darkCard,
              border: `1px solid ${COLORS.grey}`,
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Title
                level={3}
                style={{ color: COLORS.textColor, marginTop: 12 }}
              >
                {t("loadingTest")}
              </Title>
              <Paragraph style={{ color: COLORS.lightText, fontSize: 14 }}>
                {t("preparingTest")}
              </Paragraph>
            </Space>
          </Card>
        </div>
      </>
    );
  }

  if (hasError) {
    const isNotImplemented = hasError === "NOT_IMPLEMENTED";
    const availableLanguages = [
      { code: "en-us", name: "English" },
      { code: "es-es", name: "Spanish (Español)", comingSoon: true },
      { code: "fr-fr", name: "French (Français)", comingSoon: true },
      { code: "de-de", name: "German (Deutsch)", comingSoon: true },
    ];

    return (
      <>
        <Helmet>
          <title>Error | Language Placement Test</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            padding: "12px",
            background: COLORS.background,
          }}
        >
          <Card
            style={{
              maxWidth: 600,
              width: "100%",
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              background: COLORS.darkCard,
              border: `1px solid ${COLORS.grey}`,
            }}
          >
            <Space
              direction="vertical"
              size="large"
              style={{ width: "100%", textAlign: "center" }}
            >
              {isNotImplemented ? (
                <>
                  <Title
                    level={3}
                    style={{
                      color: COLORS.textColor,
                      marginTop: 12,
                      textAlign: "center",
                    }}
                  >
                    {t("testNotImplemented")}
                  </Title>
                  <Paragraph style={{ color: COLORS.lightText, fontSize: 14 }}>
                    {t("availableLanguages")}
                  </Paragraph>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%", marginTop: 16 }}
                  >
                    {availableLanguages.map((lang) => {
                      const isEnglish = lang.code === "en-us";
                      const currentParams = new URLSearchParams(
                        window.location.search,
                      );
                      const newUrl = `/placement-test?targetLanguage=${
                        lang.code
                      }${
                        currentParams.get("token")
                          ? `&token=${currentParams.get("token")}`
                          : ""
                      }${
                        currentParams.get("app_lang")
                          ? `&app_lang=${currentParams.get("app_lang")}`
                          : ""
                      }`;

                      return (
                        <div
                          key={lang.code}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            background: COLORS.secondary,
                            borderRadius: 8,
                            border: `1px solid ${COLORS.grey}`,
                            opacity: isEnglish ? 1 : 0.6,
                          }}
                        >
                          <Text
                            style={{
                              color: isEnglish
                                ? COLORS.textColor
                                : COLORS.lightText,
                              fontSize: 15,
                            }}
                          >
                            {lang.name}
                          </Text>
                          {isEnglish ? (
                            <a
                              href={newUrl}
                              style={{
                                color: COLORS.primary,
                                fontSize: 14,
                                fontWeight: 500,
                                textDecoration: "none",
                              }}
                            >
                              {t("startTest")}
                            </a>
                          ) : (
                            <Text
                              style={{
                                color: COLORS.lightText,
                                fontSize: 12,
                                fontStyle: "italic",
                              }}
                            >
                              {t("comingSoon")}
                            </Text>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                </>
              ) : (
                <>
                  <Title
                    level={3}
                    style={{ color: COLORS.textColor, marginTop: 12 }}
                  >
                    Oops!
                  </Title>
                  <Paragraph style={{ color: COLORS.lightText, fontSize: 14 }}>
                    {hasError}
                  </Paragraph>
                </>
              )}
            </Space>
          </Card>
        </div>
      </>
    );
  }

  if (isCompleted && result) {
    return (
      <>
        <Helmet>
          <title>
            Test Results: {result.level} Level | Language Placement Test
          </title>
          <meta
            name="description"
            content={`You achieved ${result.level} (${result.label}) level with a score of ${result.score}%. Test your language proficiency with our free placement test.`}
          />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: token ? "flex-start" : "center",
            alignItems: "center",
            minHeight: "100vh",
            padding: token && isMobile ? "12px 12px 90px 12px" : "20px",
            background: COLORS.background,
            position: "relative",
          }}
        >
          <Card
            style={{
              maxWidth: 600,
              width: "100%",
              textAlign: "center",
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              background: COLORS.darkCard,
              border: `1px solid ${COLORS.grey}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginTop: token ? "12px" : "0",
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <TrophyOutlined
                style={{ fontSize: 56, color: COLORS.green, marginTop: 12 }}
              />
              <Title
                level={2}
                style={{ color: COLORS.textColor, marginBottom: 8 }}
              >
                🎉 {t("greatJob")}
              </Title>
              <Paragraph
                style={{
                  fontSize: 15,
                  color: COLORS.lightText,
                  marginBottom: 8,
                }}
              >
                {t("yourEstimatedLevel")}
              </Paragraph>
              <div
                style={{
                  background: `linear-gradient(135deg, ${COLORS.primary}22, ${COLORS.accentPink}22)`,
                  padding: "20px",
                  borderRadius: 12,
                  margin: "12px 0",
                  border: `2px solid ${COLORS.primary}`,
                }}
              >
                <Title level={1} style={{ margin: 0, color: COLORS.primary }}>
                  {result.level}
                </Title>
                <Text style={{ fontSize: 18, color: COLORS.textColor }}>
                  {result.label}
                </Text>
              </div>
              <Paragraph
                style={{
                  fontSize: 15,
                  color: COLORS.textColor,
                  marginBottom: 8,
                }}
              >
                {t("score")}{" "}
                <strong style={{ color: COLORS.primary }}>
                  {result.score}%
                </strong>
              </Paragraph>
              <Paragraph
                style={{
                  color: COLORS.lightText,
                  fontSize: 14,
                  marginBottom: 12,
                }}
              >
                {token
                  ? t("testEvaluated").replace("{language}", languageName)
                  : t("greatWorkCompleted").replace("{language}", languageName)}
              </Paragraph>
              {!token && (
                <Space size="middle" style={{ marginTop: 12 }}>
                  <Button
                    size="large"
                    onClick={handleTryAgain}
                    style={{
                      background: COLORS.secondary,
                      color: COLORS.textColor,
                      borderColor: COLORS.grey,
                    }}
                  >
                    {t("tryAgain")}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleContinue}
                    icon={<HomeOutlined />}
                    style={{
                      background: COLORS.primary,
                      borderColor: COLORS.primary,
                    }}
                  >
                    {t("backToHome")}
                  </Button>
                </Space>
              )}
            </Space>
          </Card>
          {!token && <DownloadAppSection t={t} />}
          {token && (
            <div
              style={{
                position: isMobile ? "fixed" : "relative",
                bottom: isMobile ? 0 : "auto",
                left: isMobile ? 0 : "auto",
                right: isMobile ? 0 : "auto",
                padding: "12px 16px",
                background: COLORS.background,
                borderTop: isMobile ? `1px solid ${COLORS.grey}` : "none",
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                zIndex: isMobile ? 1000 : "auto",
                width: isMobile ? "100%" : "auto",
                marginTop: isMobile ? 0 : "20px",
              }}
            >
              <Button
                size="large"
                onClick={handleTryAgain}
                style={{
                  flex: 1,
                  maxWidth: isMobile ? 300 : 400,
                  background: COLORS.secondary,
                  color: COLORS.textColor,
                  borderColor: COLORS.grey,
                  height: 44,
                }}
              >
                {t("tryAgain")}
              </Button>
            </div>
          )}
        </div>
      </>
    );
  }

  if (!showPreview) {
    return (
      <>
        <Helmet>
          <title>
            {translatedLanguageName} {t("placementTest")} |{" "}
            {t("testYourLanguageProficiency")} (A1-C2) | LinguaTalk
          </title>
          <meta
            name="description"
            content={`${translatedLanguageName} ${t("placementTest")}. ${t(
              "testYourLanguageProficiency",
            )} across CEFR levels A1 to C2 in just 8 minutes.`}
          />
          <meta
            name="keywords"
            content={`${languageName} test, placement test, language level test, CEFR test, ${languageName} proficiency, A1 A2 B1 B2 C1 C2, language assessment`}
          />
          <meta
            property="og:title"
            content={`${translatedLanguageName} ${t("placementTest")} | ${t(
              "testYourLanguageProficiency",
            )}`}
          />
          <meta
            property="og:description"
            content={`${t(
              "testYourLanguageProficiency",
            )} ${translatedLanguageName} with our free 8-minute ${t(
              "placementTest",
            )}. Covers all CEFR levels from A1 to C2.`}
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:image"
            content="/assets/images/placement-test-og.png"
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${translatedLanguageName} ${t(
              "placementTest",
            )} | Free Language Assessment`}
          />
          <meta
            name="twitter:description"
            content={`${t(
              "testYourLanguageProficiency",
            )} ${translatedLanguageName} with our comprehensive ${t(
              "placementTest",
            )}. Get instant results for levels A1-C2.`}
          />
          <link
            rel="canonical"
            href={`https://linguatalk.app/placement-test?targetLanguage=${languageCode}`}
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              name: `${translatedLanguageName} ${t("placementTest")}`,
              description: `Free online ${translatedLanguageName} ${t(
                "placementTest",
              )} to evaluate your language proficiency across CEFR levels A1 to C2`,
              applicationCategory: "EducationalApplication",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "4.8",
                ratingCount: "1250",
              },
            })}
          </script>
        </Helmet>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            minHeight: "100vh",
            padding: token && isMobile ? "12px 12px 90px 12px" : "20px",
            flexDirection: "column",
            background: COLORS.background,
            position: "relative",
          }}
        >
          <Card
            style={{
              maxWidth: 600,
              width: "100%",
              borderRadius: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              background: COLORS.darkCard,
              border: `1px solid ${COLORS.grey}`,
              marginTop: token ? "12px" : "0",
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                      marginLeft: 45,
                      color: COLORS.textColor,
                    }}
                  >
                    {t("testForLanguage")} {translatedLanguageName}
                  </Title>
                  {userName && (
                    <Space style={{ marginTop: 2 }}>
                      <UserOutlined
                        style={{ color: COLORS.lightText, fontSize: 12 }}
                      />
                      <Text style={{ color: COLORS.lightText, fontSize: 12 }}>
                        {userName}
                      </Text>
                    </Space>
                  )}
                </div>
                <Space>
                  <ClockCircleOutlined
                    style={{ fontSize: 16, color: COLORS.primary }}
                  />
                  <Text
                    strong
                    style={{
                      fontSize: 16,
                      color: timeLeft < 60 ? COLORS.red : COLORS.primary,
                    }}
                  >
                    {formatTime(timeLeft)}
                  </Text>
                </Space>
              </div>

              <div
                style={{
                  background: COLORS.secondary,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `1px solid ${COLORS.grey}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space size="small">
                  <Text style={{ color: COLORS.lightText, fontSize: 13 }}>
                    {t("level")}{" "}
                    <strong style={{ color: COLORS.primary }}>
                      {currentQuestion.level}
                    </strong>
                  </Text>
                  <Text style={{ color: COLORS.lightText }}>•</Text>
                  <Text style={{ color: COLORS.lightText, fontSize: 13 }}>
                    {t("type")}{" "}
                    <strong style={{ color: COLORS.orange, marginLeft: 4 }}>
                      {t(
                        currentQuestion.type as
                          | "grammar"
                          | "vocabulary"
                          | "listening",
                      )}
                    </strong>
                  </Text>
                </Space>
              </div>

              <div style={{ minHeight: 180 }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    padding: "6px 12px",
                    borderRadius: 8,
                    marginBottom: 16,
                    display: "inline-block",
                  }}
                >
                  <Title
                    level={5}
                    style={{ margin: 0, color: COLORS.textColor, fontSize: 14 }}
                  >
                    {t("questionOf")
                      .replace("{current}", String(currentStep + 1))
                      .replace("{total}", String(questions.length))
                      .replace(
                        "{answered}",
                        String(Object.keys(answers).length),
                      )}
                  </Title>
                </div>

                {currentQuestion.type === "listening" && (
                  <Space size="small" style={{ marginBottom: 12 }}>
                    <Button
                      onClick={playAudio}
                      loading={audioPlaying}
                      disabled={audioPlaying}
                      style={{
                        background: audioPlaying ? COLORS.grey : COLORS.orange,
                        borderColor: audioPlaying ? COLORS.grey : COLORS.orange,
                        color: COLORS.white,
                      }}
                      icon={<SoundOutlined />}
                    >
                      {t("playAudio")}
                    </Button>
                    <Button
                      onClick={stopAudio}
                      disabled={!audioPlaying}
                      style={{
                        background: audioPlaying ? COLORS.red : COLORS.grey,
                        borderColor: audioPlaying ? COLORS.red : COLORS.grey,
                        color: COLORS.white,
                        opacity: audioPlaying ? 1 : 0.5,
                      }}
                      icon={<StopOutlined />}
                    >
                      Stop
                    </Button>
                  </Space>
                )}

                <Paragraph
                  style={{
                    fontSize: 15,
                    marginBottom: 16,
                    color: COLORS.textColor,
                    lineHeight: "1.6",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxHeight: "4.8em",
                    direction: "ltr",
                    textAlign: "left",
                  }}
                >
                  {currentQuestion.question}
                </Paragraph>

                <Radio.Group
                  onChange={handleAnswerChange}
                  value={answers[currentQuestion.id]}
                  style={{ width: "100%" }}
                >
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="small"
                  >
                    {currentQuestion.options.map((option, index) => (
                      <Radio
                        key={index}
                        value={index}
                        style={{
                          display: "block",
                          padding: "10px 12px",
                          borderRadius: 8,
                          border: `1px solid ${COLORS.grey}`,
                          background:
                            answers[currentQuestion.id] === index
                              ? `${COLORS.primary}22`
                              : COLORS.secondary,
                          marginBottom: 6,
                          color: COLORS.textColor,
                          transition: "all 0.3s ease",
                          direction: "ltr",
                          textAlign: "left",
                        }}
                      >
                        <span
                          style={{
                            color: COLORS.textColor,
                            fontSize: 14,
                            direction: "ltr",
                            textAlign: "left",
                          }}
                        >
                          {option}
                        </span>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>
            </Space>
          </Card>
          <div
            style={{
              position: isMobile ? "fixed" : "relative",
              bottom: isMobile ? 0 : "auto",
              left: isMobile ? 0 : "auto",
              right: isMobile ? 0 : "auto",
              padding: "12px 16px",
              background: COLORS.background,
              borderTop: isMobile ? `1px solid ${COLORS.grey}` : "none",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              zIndex: isMobile ? 1000 : "auto",
              width: isMobile ? "100%" : "auto",
              marginTop: isMobile ? 0 : "20px",
            }}
          >
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              size="large"
              style={{
                flex: 1,
                background: COLORS.secondary,
                color: COLORS.textColor,
                borderColor: COLORS.grey,
                height: 44,
              }}
            >
              {t("previous")}
            </Button>
            {currentStep === questions.length - 1 ? (
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={Object.keys(answers).length !== questions.length}
                size="large"
                style={{
                  flex: 1,
                  background: COLORS.green,
                  borderColor: COLORS.green,
                  height: 44,
                }}
              >
                {t("submitTest")}
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleNext}
                size="large"
                style={{
                  flex: 1,
                  background: COLORS.primary,
                  borderColor: COLORS.primary,
                  height: 44,
                }}
              >
                {t("next")}
              </Button>
            )}
          </div>
          {!token && <DownloadAppSection t={t} />}
        </div>
      </>
    );
  }

  return null;
};

const PlacementTestWithProvider: React.FC = () => {
  return (
    <LanguageProvider>
      <PlacementTest />
    </LanguageProvider>
  );
};

export default PlacementTestWithProvider;
